import Section from "@bmi-digital/components/section";
import { useLocation } from "@reach/router";
import { graphql } from "gatsby";
import React, { useEffect } from "react";
import BackToResults from "../../components/BackToResults";
import Breadcrumbs from "../../components/Breadcrumbs";
import Page from "../../components/Page";
import RegistrationCompletedDialog from "./components/RegistrationCompletedDialog";
import TrainingDetail from "./components/TrainingDetail";
import { StyledTopBreadcrumbsSection } from "./trainingDetailsPageStyles";
import type { PageData } from "../../components/Page";
import type { WindowLocation } from "@reach/router";
import type { HomePageData, SiteData } from "../../components/Site";
import type { TrainingDetailsCourseType as Course } from "./types";
import type { ImageTrainingDetail } from "../../components/image/types";
import type { PayloadTrainingRegistrationPage } from "../../types/payload";
import type { MicroCopy as MicroCopyData } from "../../components/MicroCopy";
import type { CalculatorConfig } from "../../components/pitched-roof-calculator/types";
import type { HouseType } from "../../components/visualiser/Types";
import type { GeneralErrorData } from "../general-error";
import type { CountryNavigation, SampleOrder } from "../../components/Header";
import type { Visualiser } from "../../components/Visualiser";

export type TrainingRegistrationPageData = Pick<
  PayloadTrainingRegistrationPage,
  | "path"
  | "successTitle"
  | "successDescription"
  | "registrationCompletedDialogCloseButton"
>;

export type Props = {
  pageContext: {
    courseId: number;
  };
  data: {
    doceboCourses: Course;
    payloadTrainingListerPage: {
      featuredMedia: ImageTrainingDetail | null;
    } | null;
    payloadTrainingRegistrationPage: TrainingRegistrationPageData | null;
    payloadSite: SiteData;
    allPayloadMicroCopy: {
      nodes: MicroCopyData[];
    };
    payloadPitchedRoofCalculatorConfig: CalculatorConfig | null;
    allPayloadHouseModel: {
      nodes: HouseType[];
    };
    payloadHomePage: HomePageData;
    payloadGeneralError: GeneralErrorData;
    payloadCountryNavigation: CountryNavigation;
    payloadSampleOrder: SampleOrder | null;
    payloadVisualiser: Visualiser | null;
  };
};

const TrainingDetailsPage = ({ data, pageContext }: Props) => {
  const {
    doceboCourses: { breadcrumbs, ...trainingData },
    payloadTrainingListerPage,
    payloadTrainingRegistrationPage
  } = data;

  const {
    state: historyState
  }: WindowLocation<{ showResultsModal?: boolean }> = useLocation();

  const pageData: PageData = {
    breadcrumbs: breadcrumbs,
    signupBlock: null,
    meta: null,
    path: `/t/${trainingData.slug_name}`
  };

  useEffect(() => {
    if (historyState?.showResultsModal === true) {
      history.replaceState({ showResultsModal: false }, "");
    }
  }, [historyState]);

  return (
    <Page
      title={trainingData.name}
      pageData={pageData}
      siteData={data.payloadSite}
      microCopy={data.allPayloadMicroCopy.nodes}
      pitchedRoofCalculatorConfig={data.payloadPitchedRoofCalculatorConfig}
      visualiserHouseTypes={data.allPayloadHouseModel.nodes}
      homePage={data.payloadHomePage}
      generalError={data.payloadGeneralError}
      countryNavigation={data.payloadCountryNavigation}
      sampleOrder={data.payloadSampleOrder}
      visualiser={data.payloadVisualiser}
    >
      <StyledTopBreadcrumbsSection
        data-testid="breadcrumbs-section-top"
        backgroundColor="pearl"
      >
        <BackToResults isDarkThemed>
          <Breadcrumbs
            isDarkThemed
            data={breadcrumbs}
            data-testid="breadcrumbs-top"
          />
        </BackToResults>
      </StyledTopBreadcrumbsSection>
      <TrainingDetail
        course={trainingData}
        courseId={pageContext.courseId}
        fallbackImageData={payloadTrainingListerPage?.featuredMedia}
      />
      <Section data-testid="breadcrumbs-section-bottom" backgroundColor="white">
        <BackToResults>
          <Breadcrumbs data={breadcrumbs} data-testid="breadcrumbs-bottom" />
        </BackToResults>
      </Section>
      <RegistrationCompletedDialog
        title={payloadTrainingRegistrationPage?.successTitle}
        description={payloadTrainingRegistrationPage?.successDescription}
        closeButtonLabel={
          payloadTrainingRegistrationPage?.registrationCompletedDialogCloseButton
        }
        open={Boolean(historyState?.showResultsModal)}
      />
    </Page>
  );
};

export default TrainingDetailsPage;

export const pageQuery = graphql`
  query TrainingDetailsPage($courseId: Int!) {
    doceboCourses(id_course: { eq: $courseId }) {
      id_course
      name
      description
      code
      categoryName
      currencySymbol
      price
      course_type
      img_url
      sessions {
        id
        code
        name
        date_start
        date_end
      }
      breadcrumbs {
        id
        label
        slug
      }
    }
    payloadTrainingListerPage {
      featuredMedia {
        ...ImageTrainingDetailFragment
      }
    }
    payloadTrainingRegistrationPage {
      path
      successTitle
      successDescription
      registrationCompletedDialogCloseButton
    }
    payloadSite {
      ...SiteFragment
    }
    allPayloadMicroCopy {
      nodes {
        ...MicroCopyFragment
      }
    }
    payloadPitchedRoofCalculatorConfig {
      ...PitchedRoofCalculatorFragment
    }
    allPayloadHouseModel {
      nodes {
        ...VisualiserHouseFragment
      }
    }
    payloadHomePage {
      ...HomePageFragment
    }
    payloadGeneralError {
      ...GeneralErrorFragment
    }
    payloadCountryNavigation {
      ...CountryNavigationFragment
    }
    payloadSampleOrder {
      ...SampleOrderFragment
    }
    payloadVisualiser {
      ...VisualiserFragment
    }
  }
`;
